import { useEffect, useRef, useState } from 'react'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { useSearchParams } from 'shared/hooks'
import { currentSpreadsheetIdAtom } from 'domains/scope-and-sequence/features/parser/atoms'
import {
  exportedSpreadsheetIdAtom,
  selectedFrameworkAtom,
  treeStructureFlowIdAtom,
} from './atoms'
import { lessonMappingTreesAtom } from 'domains/scope-and-sequence/features/lesson-mappings/atoms'
import { useAllFrameworks } from 'domains/scope-and-sequence/hooks'

const URL_PARAM_KEYS = {
  sheetId: 'sheetId',
  frameworkId: 'frameworkId',
  treeIds: 'treeIds',
  jobId: 'jobId',
  exportedSheetId: 'exportedSheetId',
  treeSource: 'treeSource', // 'manual' or spreadsheet ID
} as const

// 'manual' = fresh workflow, 'spreadsheet' = spreadsheet mode without ID yet, string = spreadsheet ID
export type TreeSourceMode = 'manual' | 'spreadsheet' | string

export function getTreeSourceFromParams(
  urlParams: URLSearchParams,
): TreeSourceMode {
  const value = urlParams.get(URL_PARAM_KEYS.treeSource)
  if (!value || value === 'manual') return 'manual'
  return value // 'spreadsheet' or actual spreadsheet ID
}

export function isSpreadsheetMode(treeSource: TreeSourceMode): boolean {
  return treeSource !== 'manual'
}

export function hasSpreadsheetId(treeSource: TreeSourceMode): boolean {
  return treeSource !== 'manual' && treeSource !== 'spreadsheet'
}

type TreeUrlData = { treeId: string; versionId: string; title: string }

function encodeTreeIds(trees: TreeUrlData[]): string {
  return trees.map((t) => `${t.treeId}|${t.versionId}|${t.title}`).join(',')
}

function decodeTreeIds(encoded: string): TreeUrlData[] {
  if (!encoded) return []
  return encoded.split(',').map((part) => {
    const [treeId, versionId, title] = part.split('|')
    return { treeId, versionId, title: title || '' }
  })
}

export function useTreeSourceMode() {
  const [urlParams, setUrlParams] = useSearchParams()

  const treeSource = getTreeSourceFromParams(urlParams)
  const isSpreadsheet = isSpreadsheetMode(treeSource)
  // Only return actual spreadsheet ID, not 'spreadsheet' placeholder
  const spreadsheetId = hasSpreadsheetId(treeSource) ? treeSource : undefined

  const setTreeSource = (source: TreeSourceMode) => {
    setUrlParams({ [URL_PARAM_KEYS.treeSource]: source })
  }

  const switchToSpreadsheet = (spreadsheetId: string) => {
    setUrlParams({ [URL_PARAM_KEYS.treeSource]: spreadsheetId })
  }

  // Switch to spreadsheet mode without an ID yet (shows URL input form)
  const switchToSpreadsheetPending = () => {
    setUrlParams({ [URL_PARAM_KEYS.treeSource]: 'spreadsheet' })
  }

  const switchToManual = () => {
    setUrlParams({ [URL_PARAM_KEYS.treeSource]: undefined })
  }

  return {
    treeSource,
    isSpreadsheet,
    spreadsheetId,
    hasSpreadsheetId: hasSpreadsheetId(treeSource),
    setTreeSource,
    switchToSpreadsheet,
    switchToSpreadsheetPending,
    switchToManual,
  }
}

export function useScopeAndSequenceUrlParams() {
  const [urlParams, setUrlParams] = useSearchParams()
  const [initialized, setInitialized] = useState(false)
  const hasInitializedRef = useRef(false)

  // Atoms
  const [sheetId, setSheetId] = useAtom(currentSpreadsheetIdAtom)
  const [selectedFramework, setSelectedFramework] = useAtom(
    selectedFrameworkAtom,
  )
  const [trees, setTrees] = useAtom(lessonMappingTreesAtom)
  const [jobId, setJobId] = useAtom(treeStructureFlowIdAtom)
  const [exportedSheetId, setExportedSheetId] = useAtom(
    exportedSpreadsheetIdAtom,
  )

  // For restoring framework from ID
  const allFrameworks = useAllFrameworks()

  // Refs for URL reading/writing across all effects.
  // urlParams and setUrlParams change reference after every navigation, so depending
  // on them in effects creates a feedback loop (write URL → new refs → effects re-fire
  // → write URL → ...). Using refs breaks this cycle.
  const urlParamsRef = useRef(urlParams)
  urlParamsRef.current = urlParams
  const setUrlParamsRef = useRef(setUrlParams)
  setUrlParamsRef.current = setUrlParams
  const hasRestoredFrameworkRef = useRef(false)

  // Effect 1: Initialize atoms from URL params on mount (non-async params)
  useEffect(() => {
    if (hasInitializedRef.current) return
    hasInitializedRef.current = true

    const params = urlParamsRef.current
    const urlSheetId = params.get(URL_PARAM_KEYS.sheetId)
    const urlTreeIds = params.get(URL_PARAM_KEYS.treeIds)
    const urlJobId = params.get(URL_PARAM_KEYS.jobId)
    const urlExportedSheetId = params.get(URL_PARAM_KEYS.exportedSheetId)

    if (urlSheetId && !sheetId) {
      setSheetId(urlSheetId)
    }

    if (urlTreeIds && trees.length === 0) {
      const decodedTrees = decodeTreeIds(urlTreeIds)
      if (decodedTrees.length > 0) {
        setTrees(decodedTrees)
      }
    }

    if (urlJobId && !jobId) {
      setJobId(urlJobId)
    }

    if (urlExportedSheetId && !exportedSheetId) {
      setExportedSheetId(urlExportedSheetId)
    }

    setInitialized(true)
    // Runs once on mount (guarded by ref). Atom setters are stable Jotai references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Effect 2: Restore framework from URL param on initial load only.
  // Only runs once (guarded by ref) to prevent a loop where FrameworkPicker
  // clears the framework (jurisdiction mismatch) and this effect re-restores it.
  useEffect(() => {
    if (hasRestoredFrameworkRef.current) return
    if (!allFrameworks) return
    const urlFrameworkId = urlParamsRef.current.get(URL_PARAM_KEYS.frameworkId)
    if (!urlFrameworkId) {
      hasRestoredFrameworkRef.current = true
      return
    }
    const framework = allFrameworks.find((f) => f.id === Number(urlFrameworkId))
    if (framework) {
      setSelectedFramework(framework)
    }
    hasRestoredFrameworkRef.current = true
  }, [allFrameworks, setSelectedFramework])

  // Effect 3: Sync atoms → URL when they change (only after initialization).
  // Uses refs to read/write URL without depending on urlParams/setUrlParams.
  useEffect(() => {
    if (!initialized) return

    const currentParams = urlParamsRef.current
    const updates: Record<string, string | undefined> = {}

    const currentUrlSheetId =
      currentParams.get(URL_PARAM_KEYS.sheetId) ?? undefined
    if (sheetId !== currentUrlSheetId) {
      updates[URL_PARAM_KEYS.sheetId] = sheetId
    }

    const currentUrlFrameworkId =
      currentParams.get(URL_PARAM_KEYS.frameworkId) ?? undefined
    const frameworkIdStr = selectedFramework?.id?.toString()
    if (frameworkIdStr !== currentUrlFrameworkId) {
      updates[URL_PARAM_KEYS.frameworkId] = frameworkIdStr
    }

    const currentUrlTreeIds =
      currentParams.get(URL_PARAM_KEYS.treeIds) ?? undefined
    const treesEncoded = trees.length > 0 ? encodeTreeIds(trees) : undefined
    if (treesEncoded !== currentUrlTreeIds) {
      updates[URL_PARAM_KEYS.treeIds] = treesEncoded
    }

    const currentUrlJobId = currentParams.get(URL_PARAM_KEYS.jobId) ?? undefined
    if (jobId !== currentUrlJobId) {
      updates[URL_PARAM_KEYS.jobId] = jobId
    }

    const currentUrlExportedSheetId =
      currentParams.get(URL_PARAM_KEYS.exportedSheetId) ?? undefined
    if (exportedSheetId !== currentUrlExportedSheetId) {
      updates[URL_PARAM_KEYS.exportedSheetId] = exportedSheetId
    }

    if (Object.keys(updates).length > 0) {
      setUrlParamsRef.current(updates)
    }
  }, [initialized, sheetId, selectedFramework, trees, jobId, exportedSheetId])
}
